import React, { useState, useEffect } from 'react';
import API from '../lib/api';
import { Target, TrendingUp, CheckCircle, XCircle, BarChart3, Brain, Zap } from 'lucide-react';
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
    RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Cell
} from 'recharts';

export default function ModelPerformance() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        API.get('/model-performance').then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
    }, []);

    if (loading || !data) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" /></div>;

    const engineRadar = data.engine_performance?.filter(e => e.engine !== 'v4 Ensemble').map(e => ({
        engine: e.engine.split(' ')[0],
        accuracy: e.accuracy,
        fullMark: 100,
    })) || [];

    return (
        <div className="space-y-6" data-testid="model-performance">
            <div className="glass-card p-6">
                <div className="flex items-center gap-3 mb-2">
                    <Target className="w-5 h-5 text-green-400" />
                    <h2 className="heading-primary text-base md:text-xl text-white">PREDICTION ACCURACY DASHBOARD</h2>
                    <div className="badge-ai">v4.0 ENGINE</div>
                    {data.source === 'elo_chronological_backtest' && <div className="ml-2 px-2 py-0.5 text-[10px] bg-purple-500/10 border border-purple-500/20 rounded text-purple-400 tech-mono">ELO BACKTEST</div>}
                </div>
                <p className="text-sm text-white/40">{data.methodology || `AI prediction accuracy validated against ${data.total_predictions} real IPL match outcomes.`}</p>
            </div>

            {/* Big accuracy cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="glass-card p-6 text-center border-l-4 border-l-green-500" data-testid="overall-accuracy">
                    <div className="tech-mono text-[10px] text-white/30 mb-1">OVERALL ACCURACY</div>
                    <div className="heading-primary text-5xl neon-text-green">{data.overall_accuracy}%</div>
                    <div className="text-xs text-white/40 mt-1">{data.correct_predictions}/{data.total_predictions} correct</div>
                    <div className="text-[10px] text-white/20 mt-1">True out-of-sample (Elo + Form + Venue)</div>
                </div>
                <div className="glass-card p-6 text-center">
                    <div className="tech-mono text-[10px] text-white/30 mb-1">MATCHES ANALYZED</div>
                    <div className="heading-primary text-5xl text-white">{data.total_predictions}</div>
                    <div className="text-xs text-white/40 mt-1">IPL 2013-2026 completed matches</div>
                </div>
                <div className="glass-card p-6 text-center">
                    <div className="tech-mono text-[10px] text-white/30 mb-1">CORRECT PREDICTIONS</div>
                    <div className="heading-primary text-5xl text-green-400">{data.correct_predictions}</div>
                    <div className="text-xs text-white/40 mt-1">
                        {data.total_predictions - data.correct_predictions} incorrect
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Engine Performance Radar */}
                <div className="glass-card p-6" data-testid="engine-radar">
                    <div className="flex items-center gap-2 mb-4">
                        <Brain className="w-4 h-4 text-purple-400" />
                        <h3 className="heading-primary text-sm text-white">AI ENGINE ACCURACY</h3>
                    </div>
                    <ResponsiveContainer width="100%" height={280}>
                        <RadarChart data={engineRadar}>
                            <PolarGrid stroke="#222" />
                            <PolarAngleAxis dataKey="engine" tick={{ fill: '#999', fontSize: 10 }} />
                            <PolarRadiusAxis domain={[50, 100]} tick={{ fill: '#666', fontSize: 9 }} />
                            <Radar name="Accuracy" dataKey="accuracy" stroke="#00E5FF" fill="#00E5FF" fillOpacity={0.2} strokeWidth={2} />
                        </RadarChart>
                    </ResponsiveContainer>
                    <div className="space-y-2 mt-2">
                        {data.engine_performance?.map((e, i) => (
                            <div key={i} className="flex items-center justify-between text-sm p-2 bg-white/5 rounded">
                                <div className="flex items-center gap-2">
                                    <Zap className="w-3 h-3 text-cyan-400" />
                                    <span className="text-white/80">{e.engine}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className={`font-bold ${e.accuracy >= 70 ? 'text-green-400' : e.accuracy >= 60 ? 'text-yellow-400' : 'text-red-400'}`}>
                                        {e.accuracy}%
                                    </span>
                                    <span className="text-[10px] text-white/20">w:{e.weight}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Accuracy by Confidence */}
                <div className="glass-card p-6" data-testid="confidence-accuracy">
                    <div className="flex items-center gap-2 mb-4">
                        <BarChart3 className="w-4 h-4 text-yellow-400" />
                        <h3 className="heading-primary text-sm text-white">ACCURACY BY CONFIDENCE</h3>
                    </div>
                    <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={data.accuracy_by_confidence}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                            <XAxis dataKey="confidence" tick={{ fill: '#999', fontSize: 10 }} />
                            <YAxis domain={[0, 100]} tick={{ fill: '#666', fontSize: 10 }} />
                            <Tooltip contentStyle={{ background: '#000', border: '1px solid #333', borderRadius: 8 }} labelStyle={{ color: '#fff' }} />
                            <Bar dataKey="accuracy" radius={[4, 4, 0, 0]}>
                                {data.accuracy_by_confidence?.map((_, i) => (
                                    <Cell key={i} fill={['#FF6B35', '#FFB830', '#39FF14', '#00E5FF', '#A855F7'][i]} opacity={0.7} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                    <div className="space-y-1 mt-3">
                        {data.accuracy_by_confidence?.map((c, i) => (
                            <div key={i} className="flex items-center justify-between text-xs p-1.5">
                                <span className="text-white/50">{c.confidence}</span>
                                <div className="flex items-center gap-2">
                                    <span className="text-white/30">{c.correct}/{c.predicted}</span>
                                    <span className={`font-bold ${c.accuracy >= 70 ? 'text-green-400' : 'text-yellow-400'}`}>{c.accuracy}%</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Team Accuracy */}
            <div className="glass-card p-6" data-testid="team-accuracy">
                <h3 className="heading-primary text-sm text-white mb-4">ACCURACY BY TEAM</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    {data.accuracy_by_team?.map((t, i) => (
                        <div key={i} className="p-3 bg-white/5 rounded-lg text-center border-t-2" style={{ borderColor: t.color }}>
                            <div className="font-bold text-sm" style={{ color: t.color }}>{t.team}</div>
                            <div className={`heading-primary text-2xl mt-1 ${t.accuracy >= 70 ? 'text-green-400' : t.accuracy >= 60 ? 'text-yellow-400' : 'text-red-400'}`}>
                                {t.accuracy}%
                            </div>
                            <div className="text-[10px] text-white/30">{t.correct}/{t.predictions} correct</div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Recent Prediction Results */}
            {data.recent_results?.length > 0 && (
                <div className="glass-card p-6" data-testid="recent-results">
                    <h3 className="heading-primary text-sm text-white mb-4">RECENT PREDICTION RESULTS</h3>
                    <div className="space-y-2 max-h-80 overflow-y-auto">
                        {data.recent_results.map((r, i) => (
                            <div key={i} className={`flex items-center justify-between p-3 rounded-lg border ${r.correct ? 'bg-green-500/5 border-green-500/10' : 'bg-red-500/5 border-red-500/10'}`}>
                                <div className="flex items-center gap-2">
                                    {r.correct ? <CheckCircle className="w-4 h-4 text-green-400" /> : <XCircle className="w-4 h-4 text-red-400" />}
                                    <span className="text-sm text-white">{r.match}</span>
                                </div>
                                <div className="flex items-center gap-3 text-xs">
                                    <span className="text-white/30">{r.date}</span>
                                    <span className="text-cyan-400">Pred: {r.predicted}</span>
                                    <span className="text-white/60">Actual: {r.actual}</span>
                                    <span className="text-white/30">{r.confidence}%</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Venue Accuracy */}
            <div className="glass-card p-6" data-testid="venue-accuracy">
                <h3 className="heading-primary text-sm text-white mb-4">ACCURACY BY VENUE</h3>
                <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={data.accuracy_by_venue?.slice(0, 10)} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                        <XAxis type="number" domain={[0, 100]} tick={{ fill: '#666', fontSize: 10 }} />
                        <YAxis type="category" dataKey="venue" width={150} tick={{ fill: '#999', fontSize: 10 }} />
                        <Tooltip contentStyle={{ background: '#000', border: '1px solid #333', borderRadius: 8 }} labelStyle={{ color: '#fff' }} />
                        <Bar dataKey="accuracy" fill="#00E5FF" opacity={0.7} radius={[0, 4, 4, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}
